import type { Card, CardDetails, ProjectConfig, User } from '~/types/shared'

export type DevMockScenario = 'offer' | 'paid' | 'issuing' | 'card'

const storageKey = 'stark-card-dev-mock'
const languageKey = 'stark-card-dev-language'
let parsedSearch: string | null = null

export function getDevMockScenario(): DevMockScenario | null {
	if (!import.meta.dev || !import.meta.client) return null

	if (window.location.search !== parsedSearch) {
		parsedSearch = window.location.search
		const requested = new URLSearchParams(window.location.search).get('mock')
		if (requested === 'off') sessionStorage.removeItem(storageKey)
		else if (requested && ['offer', 'paid', 'issuing', 'card'].includes(requested)) {
			sessionStorage.setItem(storageKey, requested)
		}
	}

	const scenario = sessionStorage.getItem(storageKey)
	return scenario === 'offer' || scenario === 'paid' || scenario === 'issuing' || scenario === 'card'
		? scenario
		: null
}

function setDevMockScenario(scenario: DevMockScenario) {
	sessionStorage.setItem(storageKey, scenario)
}

const mockConfig: ProjectConfig = {
	tg_support_account_url: 'https://t.me/starkcryptobank_bot',
	user_manual_url: { ru: '', en: '' },
	green_card: {
		user_issue_price_usdt: 35,
		auto_topup_after_issue_amount_usdt: 10,
		min_topup_amount_usdt: 12,
		trc20_topup_fixed_fee_amount_usdt: 2.5,
		ton_topup_min_fee_amount_usdt: 0,
		usdt_topup_fee_percent: 2.3,
		trc20_min_topup_deposit_amount_usdt: 15,
		spend_one_tx_limit_usdt: 5000,
		spend_daily_limit_usdt: 30000,
		spend_monthly_limit_usdt: 300000,
	},
	blue_card: {
		is_issue_enabled: true,
		user_issue_price_usdt: 35,
		auto_topup_after_issue_amount_usdt: 10,
		min_topup_amount_usdt: 12,
		trc20_topup_fixed_fee_amount_usdt: 2.5,
		ton_topup_min_fee_amount_usdt: 0,
		usdt_topup_fee_percent: 2.3,
		trc20_min_topup_deposit_amount_usdt: 15,
		spend_one_tx_limit_usdt: 5000,
		spend_daily_limit_usdt: 30000,
		spend_monthly_limit_usdt: 300000,
		tx_auth_fee_usdt: 0.2,
		tx_lt_30_usdt_fee_usdt: 0.3,
	},
}

export const mockCardDetails: CardDetails = {
	billingAddress: 'Demo street 1',
	billingCity: 'Demo city',
	billingCountry: 'Demo',
	billingState: 'Demo',
	billingZipCode: '00000',
	firstName: 'DEMO',
	lastName: 'USER',
	cardNo: '4111111111111111',
	expiryDate: '12/30',
	cardCvv: '123',
}

export function getDevMockProfile() {
	const scenario = getDevMockScenario()
	const language_code = sessionStorage.getItem(languageKey) === 'en' ? 'en' : 'ru'
	const user: User = {
		_id: { $oid: '000000000000000000000001' },
		tg: { id: 0, first_name: 'Demo', last_name: '', username: 'demo', language_code },
		email: 'demo@example.test',
		language_code,
		usdt_balance: 50,
	}
	const blue_c_state: Card | null = scenario === 'offer' || !scenario
		? null
		: { status: scenario === 'card' ? 'active' : scenario, card_id: { $oid: '000000000000000000000002' }, label: '1111' }

	return {
		success: true,
		user,
		project_config: mockConfig,
		blue_c_state,
		tron_wallet_address: 'DEMO-ADDRESS-DO-NOT-SEND',
		txs_last: { items: [] },
	}
}

export function getDevMockResponse(endpoint: string, body?: any) {
	if (endpoint === '/api/hybrid/blue_c_issue_pay') setDevMockScenario('paid')
	if (endpoint === '/api/hybrid/blue_c_issue_kyc') setDevMockScenario('issuing')
	if (endpoint === '/api/hybrid/update_user_language') {
		sessionStorage.setItem(languageKey, body?.code === 'en' ? 'en' : 'ru')
		return getDevMockProfile()
	}
	if (endpoint === '/api/hybrid/get_user' || endpoint === '/api/hybrid/blue_c_issue_pay' || endpoint === '/api/hybrid/blue_c_issue_kyc') {
		return getDevMockProfile()
	}
	if (endpoint === '/api/hybrid/blue_c_balance') {
		return { success: true, item: { success: true, result: { symbol: 'USD', balance: 128.4, currency: 'USD', userBankcardId: 0, refundAmt: 0 } } }
	}
	if (endpoint === '/api/hybrid/txs') return { success: true, items: [], meta: { total_items_count: 0 } }

	throw new Error(`Dev mock has no response for ${endpoint}`)
}
